// src/pages/Dashboard.js
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Link } from 'react-router-dom';
import {
  FaShoppingBag,
  FaBoxes,
  FaClock,
  FaRupeeSign,
  FaBell,
  FaTimes,
} from 'react-icons/fa';

const Dashboard = () => {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [salesData, setSalesData] = useState([]);
  const [ordersByDay, setOrdersByDay] = useState([]);
  const [orderStatusData, setOrderStatusData] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const CHART_COLORS = ['#0ea5e9', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444'];
  const STATUS_COLORS = {
    'pending': '#f59e0b',
    'confirmed': '#3b82f6',
    'processing': '#0ea5e9',
    'shipped': '#8b5cf6',
    'delivered': '#10b981',
    'cancelled': '#ef4444',
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');

        const pendingRes = await fetch(`${process.env.REACT_APP_API_URL}/api/orders/pending`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!pendingRes.ok) throw new Error('Failed to load pending orders');
        const pendingData = await pendingRes.json();
        setPendingOrders(pendingData);

        const productsRes = await fetch(`${process.env.REACT_APP_API_URL}/api/products`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!productsRes.ok) throw new Error('Failed to load products');
        const productsData = await productsRes.json();
        setTotalProducts(productsData.length);

        const ordersRes = await fetch(`${process.env.REACT_APP_API_URL}/api/orders/all`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!ordersRes.ok) throw new Error('Failed to load orders');
        const ordersData = await ordersRes.json();
        setAllOrders(ordersData);
        setTotalOrders(ordersData.length);

        const revenue = ordersData.reduce((sum, order) => {
          if (order.status !== 'cancelled') {
            return sum + (order.totalPrice || 0);
          }
          return sum;
        }, 0);
        setTotalRevenue(revenue);

        processChartData(ordersData);
        setLoading(false);
      } catch (err) {
        toast.error('Failed to load dashboard data');
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const processChartData = (orders) => {
    const last7Days = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });

      const dayOrders = orders.filter(order => {
        const orderDate = new Date(order.createdAt).toISOString().split('T')[0];
        return orderDate === dateStr && order.status !== 'cancelled';
      });

      const dayRevenue = dayOrders.reduce((sum, order) => sum + (order.totalPrice || 0), 0);

      last7Days.push({
        name: dayName,
        date: dateStr,
        sales: dayRevenue,
        orders: dayOrders.length,
      });
    }
    setSalesData(last7Days);
    setOrdersByDay(last7Days);

    const statusCounts = {};
    orders.forEach(order => {
      const status = order.status || 'Pending';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    const statusData = Object.entries(statusCounts).map(([name, value]) => ({
      name,
      value,
      color: STATUS_COLORS[name] || '#94a3b8',
    }));
    setOrderStatusData(statusData);
  };

  const StatCard = ({ icon: Icon, title, value, gradient, prefix = '' }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      className="bg-white rounded-xl p-6 shadow-card border border-slate-100 hover:shadow-soft transition-all duration-300"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
          <p className="text-2xl font-bold text-slate-800">
            {prefix}{typeof value === 'number' ? value.toLocaleString('en-IN') : value}
          </p>
        </div>
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg`}>
          <Icon className="text-white text-xl" />
        </div>
      </div>
    </motion.div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-slate-500 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Dashboard</h1>
          <p className="text-slate-500 mt-1">Welcome back! Here's what's happening.</p>
        </div>

        {/* Notifications Button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setNotificationsOpen(!notificationsOpen)}
          className="relative bg-white p-3 rounded-xl shadow-card border border-slate-100 hover:shadow-soft transition-all"
        >
          <FaBell className="text-xl text-slate-600" />
          {pendingOrders.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">
              {pendingOrders.length}
            </span>
          )}
        </motion.button>
      </div>

      {/* Notifications Panel */}
      <AnimatePresence>
        {notificationsOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className="absolute top-16 right-0 w-80 bg-white rounded-xl shadow-elevated border border-slate-100 overflow-hidden z-50"
          >
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-4 flex items-center justify-between">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <FaBell /> Notifications
              </h3>
              <button
                onClick={() => setNotificationsOpen(false)}
                className="text-white/80 hover:text-white hover:bg-white/10 p-1 rounded transition-colors"
              >
                <FaTimes />
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {pendingOrders.length === 0 ? (
                <div className="p-6 text-center text-slate-500">
                  No new notifications
                </div>
              ) : (
                pendingOrders.map((order, index) => (
                  <motion.div
                    key={order._id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="p-4 border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="bg-primary-100 p-2 rounded-lg">
                        <FaShoppingBag className="text-primary-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">New Order</p>
                        <p className="text-sm text-slate-500">
                          #{order._id.slice(-8).toUpperCase()}
                        </p>
                        <p className="text-sm font-semibold text-primary-600">
                          ₹{order.totalPrice?.toLocaleString('en-IN')}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
            {pendingOrders.length > 0 && (
              <div className="p-3 bg-slate-50 text-center border-t border-slate-100">
                <Link to="/orders" className="text-primary-600 text-sm font-medium hover:text-primary-700">
                  View All Orders
                </Link>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={FaRupeeSign}
          title="Total Revenue"
          value={totalRevenue}
          gradient="from-emerald-500 to-emerald-600"
          prefix="₹"
        />
        <StatCard
          icon={FaShoppingBag}
          title="Total Orders"
          value={totalOrders}
          gradient="from-primary-500 to-primary-600"
        />
        <StatCard
          icon={FaBoxes}
          title="Total Products"
          value={totalProducts}
          gradient="from-violet-500 to-violet-600"
        />
        <StatCard
          icon={FaClock}
          title="Pending Orders"
          value={pendingOrders.length}
          gradient="from-amber-500 to-amber-600"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Sales Trend Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white p-6 rounded-xl shadow-card border border-slate-100"
        >
          <h3 className="text-lg font-semibold text-slate-800 mb-1">Sales Trend</h3>
          <p className="text-sm text-slate-500 mb-4">Last 7 days revenue</p>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={salesData}>
              <defs>
                <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  padding: '12px',
                }}
                formatter={(value) => [`₹${value.toLocaleString('en-IN')}`, 'Revenue']}
              />
              <Area
                type="monotone"
                dataKey="sales"
                stroke="#0ea5e9"
                strokeWidth={2}
                fill="url(#salesGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Orders by Day Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white p-6 rounded-xl shadow-card border border-slate-100"
        >
          <h3 className="text-lg font-semibold text-slate-800 mb-1">Orders by Day</h3>
          <p className="text-sm text-slate-500 mb-4">Daily order count</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={ordersByDay}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  padding: '12px',
                }}
              />
              <Bar dataKey="orders" radius={[6, 6, 0, 0]}>
                {ordersByDay.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Order Status Pie Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white p-6 rounded-xl shadow-card border border-slate-100"
        >
          <h3 className="text-lg font-semibold text-slate-800 mb-1">Order Status</h3>
          <p className="text-sm text-slate-500 mb-4">Distribution by status</p>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={orderStatusData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={4}
                dataKey="value"
              >
                {orderStatusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  padding: '12px',
                }}
              />
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={(value) => <span className="text-sm text-slate-600">{value}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Recent Orders Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white p-6 rounded-xl shadow-card border border-slate-100"
        >
          <h3 className="text-lg font-semibold text-slate-800 mb-1">Recent Orders</h3>
          <p className="text-sm text-slate-500 mb-4">Latest transactions</p>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-3 px-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Order ID</th>
                  <th className="text-left py-3 px-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                  <th className="text-left py-3 px-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {allOrders.slice(0, 5).map((order) => (
                  <tr key={order._id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                    <td className="py-3 px-2 text-sm font-medium text-slate-700">
                      #{order._id.slice(-8).toUpperCase()}
                    </td>
                    <td className="py-3 px-2 text-sm font-semibold text-slate-800">
                      ₹{order.totalPrice?.toLocaleString('en-IN')}
                    </td>
                    <td className="py-3 px-2">
                      <span
                        className="px-2.5 py-1 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: `${STATUS_COLORS[order.status]}15`,
                          color: STATUS_COLORS[order.status],
                        }}
                      >
                        {order.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;
